import React, { useState } from 'react';
import { Grid, Typography, Button, CardContent, Box, useTheme, TextField, InputAdornment, IconButton } from '@mui/material';
import CustomCard from '../../components/@extended/CustomCard';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSiteByIdQuery, useUpdateSiteDetailsMutation, useGetSiteMembersQuery } from '../../store/slices/apiSlice';
import RightArrow from '../../components/icons/RightArrow';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SiteUsersTable from './SiteUsersTable';

const LabelContainer = ({ 
  label, 
  value, 
  valueFontWeight, 
  valueColor, 
  fieldName, 
  onEdit, 
  isEditing, 
  onSave, 
  onCancel, 
  onValueChange,
  editable = true 
}) => {
  const [hovered, setHovered] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(fieldName, editValue);
  };

  const handleCancel = () => {
    setEditValue(value);
    onCancel(fieldName);
  };

  const handleValueChange = (event) => {
    setEditValue(event.target.value);
    if (onValueChange) {
      onValueChange(fieldName, event.target.value);
    }
  };

  return (
    <Grid container py={1} ml={8}>
      <Grid item xs={4} md={5}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8} md={7}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            '&:hover .edit-icon': {
              opacity: editable ? 1 : 0,
            },
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {isEditing ? (
            <TextField
              value={editValue}
              onChange={handleValueChange}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSave} color="primary">
                      <SaveIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancel} color="error">
                      <CancelIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <>
              <Typography variant="body1" fontWeight={valueFontWeight} color={valueColor}>
                {value}
              </Typography>
              {editable && (
                <IconButton
                  size="small"
                  className="edit-icon"
                  onClick={() => onEdit(fieldName)}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    ml: 1,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

const SiteDetails = () => {
  const navigate = useNavigate();
  const { sponsorId, siteId } = useParams();
  const theme = useTheme();
  const { data: siteDetails, isLoading } = useGetSiteByIdQuery({ sponsorId, siteId });
  const { data: siteUsers, isLoading: isLoadingUsers } = useGetSiteMembersQuery({ sponsorId, siteId });
  const [updateSiteDetails] = useUpdateSiteDetailsMutation();
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({});

  const handleEdit = (fieldName) => {
    setEditingField(fieldName);
    setFormData(prev => ({
      ...prev,
      [fieldName]: siteDetails?.[fieldName] || ''
    }));
  };

  const handleSave = async (fieldName, value) => {
    try {
      const updateData = {
        [fieldName]: value
      };
      
      await updateSiteDetails({
        sponsorId,
        siteId,
        body: updateData
      }).unwrap();
      
      setEditingField(null);
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));
    } catch (error) {
      console.error('Error updating site details:', error);
    }
  };

  const handleCancel = (fieldName) => {
    setEditingField(null);
    setFormData(prev => {
      const newData = { ...prev };
      delete newData[fieldName];
      return newData;
    });
  };

  const handleValueChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  if (isLoading) {
    return <></>;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Button type="text" onClick={() => navigate(`/sponsorDisplay/${sponsorId}`)} startIcon={<RightArrow leftArrow />}>
          <Typography variant="subtitle1" sx={{ textTransform: 'none' }}>
            Back
          </Typography>
        </Button>
      </Grid>
      <Grid item xs={12}>
        <CustomCard
          title={
            <Typography variant="h6" fontWeight={600} color={theme.palette.grey[800]}>
              Site Details
            </Typography>
          }
        >
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={9}>
                <LabelContainer 
                  label={'Site Name'} 
                  value={siteDetails?.orgname || 'N/A'} 
                  fieldName="orgname"
                  isEditing={editingField === 'orgname'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer 
                  label={'Address'} 
                  value={siteDetails?.address || 'N/A'} 
                  fieldName="address"
                  isEditing={editingField === 'address'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer 
                  label={'Fax'} 
                  value={siteDetails?.fax || 'N/A'} 
                  fieldName="fax"
                  isEditing={editingField === 'fax'}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onValueChange={handleValueChange}
                />
                <LabelContainer
                  label={'Bank Verification Status'}
                  value={siteDetails?.financeDetail?.status === 'active' ? 'Active' : 'Pending'}
                  valueFontWeight={600}
                  valueColor={
                    siteDetails?.financeDetail?.status === 'active'
                      ? theme.palette.success.main
                      : theme.palette.warning.main
                  }
                  editable={false}
                />
              </Grid>
            </Grid>
          </CardContent>
        </CustomCard>
      </Grid>
      <Grid item sm={12}>
        <SiteUsersTable sponsorId={sponsorId} />
      </Grid>
    </Grid>
  );
};

export default SiteDetails;

